<template>
    <div class="owners-list p-2">
        <div class="owners-list-header">
            <div>
                <span class="owners-list-kicker">Administration</span>
                <h5 class="title mb-1">Comptes clients<span v-if="isArchive"> inactifs</span></h5>
                <p class="text-muted mb-0">Recherche et consultation des comptes restaurateurs.</p>
            </div>
            <div class="owners-list-actions">
                <router-link v-if="isYservices" class="btn btn-sm btn-success btn-circle" :to="{ name: 'admin.owners.create' }">
                    <feather type="plus" />
                    Nouveau client
                </router-link>
                <a class="btn btn-sm btn-outline-secondary btn-circle" href="/api/admin/exports/owners" target="_blank">
                    <feather type="download" />
                    Exporter
                </a>
                <button
                    v-if="!isArchive"
                    type="button"
                    class="btn btn-sm btn-outline-secondary btn-circle"
                    @click="showImportFiducialClientsIdsModal = true">
                    <feather type="refresh-cw" />
                    Synchroniser IWS
                </button>
            </div>
        </div>

        <LoaderComponent v-if="loading" />
        <div v-else>
            <div class="owners-list-metrics">
                <div class="owners-list-metric">
                    <span>Total affiché</span>
                    <strong>{{ ownersList.length }}</strong>
                </div>
                <div class="owners-list-metric">
                    <span>Licences actives</span>
                    <strong>{{ activeOwnersCount }}</strong>
                </div>
                <div class="owners-list-metric warning">
                    <span>Sans licence active</span>
                    <strong>{{ inactiveOwnersCount }}</strong>
                </div>
                <div class="owners-list-metric">
                    <span>Établissements</span>
                    <strong>{{ restaurantsCount }}</strong>
                </div>
            </div>

            <div class="owners-list-toolbar">
                <form class="owners-search" @submit.prevent="fetchOwnersList">
                    <feather class="owners-search-icon" type="search" />
                    <input v-model="filter" class="owners-search-input" placeholder="Établissement, nom, email..." type="text" />
                    <button v-if="filter" @click="resetFilter" type="button" class="owners-search-clear">
                        <feather type="x" />
                    </button>
                    <button type="submit" class="btn btn-sm btn-primary btn-circle">Rechercher</button>
                </form>

                <switch-input-component v-model="includeInternalAccounts" :is-form-grid="false" class="owners-internal-switch">
                    <template #label>Inclure les comptes internes</template>
                </switch-input-component>
            </div>

            <ns-table tableClass="table table-sm owners-table mb-0" allow-sorting :data-source="ownersList">
                <template #head>
                    <ns-th field="fullname" allow-sorting :sort-comparer="fullNameCompareFn"> Client </ns-th>
                    <ns-th field="last_login_at" allow-sorting :sort-comparer="lastLoginCompareFn"> Dernière connexion </ns-th>
                    <ns-th field="active_licenses_count" allow-sorting :sort-comparer="activeLicensesCompareFn"> Licences </ns-th>
                    <ns-th field="restaurants.data.length" allow-sorting :sort-comparer="restaurantsCountCompareFn"> Établissements </ns-th>
                    <ns-th field="restaurants.data[0].city" allow-sorting> Ville / CP </ns-th>
                    <ns-th> Actions </ns-th>
                </template>
                <template #body="{ data }">
                    <td>
                        <div class="owner-client-cell">
                            <div class="owner-client-heading">
                                <strong>{{ displayFullName(data) }}</strong>
                                <span class="text-muted small">#{{ data.id }}</span>
                            </div>
                            <a class="owner-client-email" :href="`mailto:${data.email}`">{{ data.email }}</a>
                            <div class="owner-client-meta">
                                Inscrit le {{ displayDate(data.created_at, DATE_SHORT) }}
                                <span v-if="data.fiducial_client_id"> · IWS {{ data.fiducial_client_id }}</span>
                            </div>
                            <span class="badge owner-status-badge" :class="statusClass(data)">{{ statusLabel(data) }}</span>
                        </div>
                    </td>
                    <td>
                        {{ displayOptionalDate(data.last_login_at, true) }}
                    </td>
                    <td>
                        <span class="badge" :class="data.active_licenses_count > 0 ? 'text-bg-success' : 'text-bg-warning'">
                            {{ data.active_licenses_count || 0 }}
                        </span>
                    </td>
                    <td>
                        <div class="owner-restaurants-cell">
                            <strong>{{ restaurantsCountForOwner(data) }}</strong>
                            <span v-if="restaurantsCountForOwner(data) > 0" v-html="joinWithBr(arrayPluck('name', data.restaurants.data))"></span>
                            <span v-else class="text-muted">Aucun établissement</span>
                        </div>
                    </td>
                    <td v-html="restaurantLocations(data)"></td>
                    <td class="owner-table-actions">
                        <button
                            @click="selectOwner(data)"
                            :disabled="data.deleted_at !== null"
                            type="button"
                            class="btn btn-sm btn-outline-secondary btn-square"
                            v-tooltip="data.deleted_at !== null ? undefined : getTooltip(`Se connecter en tant que '${data.email}'`)">
                            <feather type="log-in" />
                        </button>
                        <router-link
                            :to="{ name: 'admin.ownerDetails', params: { owner_id: data.id } }"
                            class="btn btn-sm btn-outline-secondary btn-square"
                            :class="{ disabled: data.deleted_at !== null }"
                            v-tooltip="getTooltip(`Voir les details du compte '${data.email}'`)">
                            <feather type="eye" />
                        </router-link>
                    </td>
                </template>
            </ns-table>
        </div>

        <import-fiducial-clients-ids
            v-if="showImportFiducialClientsIdsModal"
            @close="showImportFiducialClientsIdsModal = false"></import-fiducial-clients-ids>
    </div>
</template>

<script>
import LoaderComponent from "../../components/LoaderComponent.vue";
import SwitchInputComponent from "../../components/forms/SwitchInputComponent.vue";
import NsTable from "../../components/Datatable/NsTable.vue";
import NsTh from "../../components/Datatable/NsTh.vue";
import ImportFiducialClientsIds from "../../components/Modals/Admin/Clients/ImportFiducialClientsIds.vue";

export default {
    name: "OwnersList",
    data() {
        return {
            loading: false,
            ownersList: [],
            includeInternalAccounts: true,
            showImportFiducialClientsIdsModal: false,
        };
    },
    computed: {
        filter: {
            get() {
                return this.$route.query.filter || "";
            },
            set(filter) {
                if (filter !== this.filter) {
                    this.$router.push({ name: this.$route.name, query: { filter } });
                }
            },
        },
        isArchive() {
            return this.$route.name === "admin.archive";
        },
        activeOwnersCount() {
            return this.ownersList.filter((owner) => owner.has_active_subscription).length;
        },
        inactiveOwnersCount() {
            return this.ownersList.filter((owner) => !owner.has_active_subscription).length;
        },
        restaurantsCount() {
            return this.ownersList.reduce((total, owner) => total + (owner.restaurants?.data?.length || 0), 0);
        },
    },
    methods: {
        getRestaurantAddress(restaurant) {
            return (
                restaurant.google_maps_address ?? {
                    postal_code: restaurant.zipcode,
                    locality: restaurant.city,
                }
            );
        },
        fullNameCompareFn(a, b) {
            const lastnameResult = (a.lastname || "").localeCompare(b.lastname || "");

            if (lastnameResult !== 0) {
                return lastnameResult;
            }

            return (a.firstname || "").localeCompare(b.firstname || "");
        },
        lastLoginCompareFn(a, b) {
            return this.compareNullableDateDesc(a.last_login_at, b.last_login_at);
        },
        activeLicensesCompareFn(a, b) {
            return (b.active_licenses_count || 0) - (a.active_licenses_count || 0);
        },
        restaurantsCountCompareFn(a, b) {
            return this.restaurantsCountForOwner(b) - this.restaurantsCountForOwner(a);
        },
        compareNullableDateDesc(a, b) {
            if (!a && !b) {
                return 0;
            }

            if (!a) {
                return 1;
            }

            if (!b) {
                return -1;
            }

            return this.getDateTime(b).diff(this.getDateTime(a), "minutes").minutes;
        },
        displayOptionalDate(date, withTime = false) {
            if (!date) {
                return "-";
            }

            return this.displayDate(date, withTime ? this.DATETIME_SHORT : this.DATE_SHORT);
        },
        restaurantsCountForOwner(owner) {
            return owner.restaurants?.data?.length || 0;
        },
        restaurantLocations(owner) {
            const restaurants = owner.restaurants?.data || [];

            if (restaurants.length === 0) {
                return '<span class="text-muted">-</span>';
            }

            return this.joinWithBr(
                restaurants.map((restaurant) => {
                    const address = this.getRestaurantAddress(restaurant);

                    return [address.locality, address.postal_code].filter(Boolean).join(" / ") || "-";
                })
            );
        },
        displayFullName(owner) {
            const fullname = `${owner.firstname || ""} ${owner.lastname || ""}`.trim();

            return fullname || owner.company || owner.email || `Client #${owner.id}`;
        },
        statusLabel(owner) {
            if (owner.enabled_by_admin) {
                return "Activé admin";
            }

            if (owner.deleted_at !== null) {
                return "Archivé";
            }

            if (!owner.has_active_subscription) {
                return "Inactif";
            }

            return "Actif";
        },
        statusClass(owner) {
            if (owner.enabled_by_admin) {
                return "text-bg-danger";
            }

            if (owner.deleted_at !== null) {
                return "text-bg-dark";
            }

            if (!owner.has_active_subscription) {
                return "text-bg-warning";
            }

            return "text-bg-success";
        },
        resetFilter() {
            this.filter = "";
            this.fetchOwnersList();
        },
        fetchOwnersList() {
            this.loading = true;
            this.ownersList = [];

            this.httpGet(
                `/api/admin/owners?include=restaurants.google_maps_address,has_active_subscription&filter=${this.filter}&is_archive=${this.isArchive}&include_internal_accounts=${this.includeInternalAccounts}`
            )
                .then((response) => {
                    if (response !== false) {
                        this.ownersList = response.data.data;
                    }
                })
                .finally(() => (this.loading = false));
        },
        logAs(owner) {
            this.loading = true;

            this.httpPost(`/api/admin/log_as/${owner.id}`, {}, { handleResolve: false }).then((response) => {
                if (response !== false) {
                    document.location.href = "/";
                } else {
                    this.loading = false;
                }
            });
        },
        selectOwner(owner) {
            if (owner.deleted_at !== null) {
                return;
            }

            if (!confirm(`Souhaitez vous vous connecter avec l'idendifiant "${owner.email}" ?`)) {
                return;
            }

            this.logAs(owner);
        },
        joinWithBr(arr) {
            return arr.map((value) => (value !== null ? this.escapeHtml(value.toString()) : "")).join("<br/>");
        },
    },
    watch: {
        includeInternalAccounts() {
            this.fetchOwnersList();
        },
    },
    components: {
        LoaderComponent,
        SwitchInputComponent,
        NsTable,
        NsTh,
        ImportFiducialClientsIds,
    },
    created() {
        this.fetchOwnersList();
    },
};
</script>

<style>
.owners-list {
    width: 100%;
    max-width: 1440px;
    margin-right: auto;
    margin-left: auto;
}

.owners-list-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
    padding: 4px 2px 0;
}

.owners-list-kicker {
    display: inline-flex;
    margin-bottom: 6px;
    color: #157ec2;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.owners-list .title {
    color: #16273a;
    font-size: 1.35rem;
    font-weight: 700;
}

.owners-list-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
}

.owners-list-actions .feather {
    width: 14px;
    height: 14px;
    margin-right: 4px;
}

.owners-list-metrics {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}

.owners-list-metric {
    padding: 14px 16px;
    border: 1px solid #d9e4ec;
    border-radius: 6px;
    background: #fff;
}

.owners-list-metric span {
    display: block;
    color: #627184;
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
}

.owners-list-metric strong {
    display: block;
    margin-top: 6px;
    color: #182b3c;
    font-size: 1.45rem;
    line-height: 1;
}

.owners-list-metric.warning {
    border-color: #e6a55f;
}

.owners-list-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
    padding: 12px;
    border: 1px solid #d9e4ec;
    border-radius: 6px;
    background: #fff;
}

.owners-search {
    display: flex;
    align-items: center;
    flex: 1;
    max-width: 620px;
    min-height: 36px;
    padding: 0 4px 0 12px;
    border: 1px solid #ced8e1;
    border-radius: 20px;
    background: #f8fbfd;
}

.owners-search-icon {
    width: 15px;
    height: 15px;
    margin-right: 8px;
    color: #7b8a9a;
}

.owners-search-input {
    min-width: 0;
    flex: 1;
    border: 0;
    outline: 0;
    background: transparent;
    color: #182b3c;
}

.owners-search-clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin-right: 4px;
    border: 0;
    border-radius: 14px;
    background: transparent;
    color: #7b8a9a;
}

.owners-search-clear .feather {
    width: 15px;
    height: 15px;
}

.owners-internal-switch {
    margin-bottom: 0;
}

.owners-table {
    overflow: hidden;
    border: 1px solid #d9e4ec;
    border-radius: 6px;
    background: #fff;
}

.owners-table thead th {
    border-top: 0;
    background: #f4f8fb;
    color: #435568;
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
}

.owners-table tbody td {
    vertical-align: middle;
}

.owner-client-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 260px;
}

.owner-client-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
    color: #182b3c;
    text-transform: capitalize;
}

.owner-client-email {
    color: #157ec2;
    word-break: break-word;
}

.owner-client-meta {
    color: #627184;
    font-size: 0.78rem;
}

.owner-restaurants-cell {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 8px;
    align-items: start;
    min-width: 190px;
}

.owner-status-badge {
    font-size: 0.66rem;
    letter-spacing: 0;
}

.owner-table-actions {
    width: 84px;
    white-space: nowrap;
}

@media (max-width: 991.98px) {
    .owners-list-header,
    .owners-list-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .owners-list-actions {
        justify-content: flex-start;
    }

    .owners-list-metrics {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .owners-search {
        max-width: none;
    }
}

@media (max-width: 575.98px) {
    .owners-list-metrics {
        grid-template-columns: 1fr;
    }
}
</style>
